import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, getAnimation, resolveImage, hexToRgba, isColorDark, resolveLinkProps } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import SectionIcon from './SectionIcon';

export default function PersonaGridSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const items = content.items || [];
    const variant = config.variant || 'cards';
    const card = getCardClass(cardStyle, s.dark);
    const columns = config.columns || 4;

    if (items.length === 0) return null;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };
    const colClass = columns === 2 ? 'md:grid-cols-2' : columns === 3 ? 'md:grid-cols-3' : columns === 5 ? 'md:grid-cols-3 lg:grid-cols-5' : 'md:grid-cols-2 lg:grid-cols-4';

    // ── Icons variant ──
    if (variant === 'icons') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s}/>
                    <div className={`grid grid-cols-2 ${colClass} gap-6`}>
                        {items.map((item, i) => {
                            const accent = item.color || s.accent;
                            const img = resolveImage(item, 'image', mediaFolders);
                            return (
                                <a key={i} {...resolveLinkProps(item.link || '#')}
                                    className={`group flex flex-col items-center text-center p-6 rounded-xl transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{ transitionDelay: visible ? `${i * 80}ms` : '0ms' }}
                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = hexToRgba(accent, 0.06)}
                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-3 transition-transform group-hover:scale-110" style={{ backgroundColor: hexToRgba(accent, 0.1) }}>
                                        {img ? (
                                            <img src={img} alt={item.title || ''} className="w-10 h-10 object-contain" loading="lazy"/>
                                        ) : item.icon ? (
                                            <div style={{ color: accent }}><SectionIcon name={item.icon} className="w-8 h-8"/></div>
                                        ) : (
                                            <div className="text-2xl">{item.emoji || '👤'}</div>
                                        )}
                                    </div>
                                    <h3 className="text-sm font-bold" style={{ color: s.text }}>{item.title}</h3>
                                    <span className="text-xs mt-1 font-medium opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: accent }}>
                                        {item.link_text || 'Conocer mas'} →
                                    </span>
                                </a>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Hexagonal variant ──
    if (variant === 'hexagonal') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s}/>
                    <div className={`grid grid-cols-2 ${colClass} gap-8`}>
                        {items.map((item, i) => {
                            const accent = item.color || s.accent;
                            const img = resolveImage(item, 'image', mediaFolders);
                            return (
                                <a key={i} {...resolveLinkProps(item.link || '#')}
                                    className={`group text-center transition-all duration-500 hover:-translate-y-2 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}>
                                    <div className="flex justify-center mb-4">
                                        <div className="w-24 h-24 flex items-center justify-center transition-transform group-hover:scale-110"
                                            style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)', backgroundColor: hexToRgba(accent, 0.12) }}>
                                            {img ? (
                                                <img src={img} alt={item.title || ''} className="w-14 h-14 object-contain" loading="lazy"/>
                                            ) : item.icon ? (
                                                <div style={{ color: accent }}><SectionIcon name={item.icon} className="w-10 h-10"/></div>
                                            ) : (
                                                <div className="text-3xl">{item.emoji || '👤'}</div>
                                            )}
                                        </div>
                                    </div>
                                    <h3 className="text-base font-bold group-hover:underline" style={{ color: s.text }}>{item.title}</h3>
                                    {item.description && <p className="text-sm mt-1 line-clamp-2" style={{ color: s.textSub }}>{item.description}</p>}
                                    <span className="inline-flex items-center text-xs font-semibold mt-2 group-hover:gap-1.5 transition-all" style={{ color: accent }}>
                                        {item.link_text || 'Conocer mas'} <span className="group-hover:translate-x-1 transition-transform">→</span>
                                    </span>
                                </a>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Hover-reveal variant ──
    if (variant === 'hover-reveal') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s}/>
                    <div className={`grid grid-cols-2 ${colClass} gap-6`}>
                        {items.map((item, i) => {
                            const accent = item.color || s.accent;
                            const img = resolveImage(item, 'image', mediaFolders);
                            const dark = isColorDark(accent);
                            return (
                                <a key={i} {...resolveLinkProps(item.link || '#')}
                                    className={`group relative ${card} overflow-hidden p-6 min-h-[160px] flex flex-col items-center justify-center text-center transition-all duration-500 hover:shadow-xl ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}>
                                    {/* Normal state */}
                                    <div className="transition-all duration-300 group-hover:opacity-0 group-hover:-translate-y-4">
                                        <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: hexToRgba(accent, 0.1) }}>
                                            {img ? (
                                                <img src={img} alt={item.title || ''} className="w-8 h-8 object-contain" loading="lazy"/>
                                            ) : item.icon ? (
                                                <div style={{ color: accent }}><SectionIcon name={item.icon} className="w-7 h-7"/></div>
                                            ) : (
                                                <div className="text-xl">{item.emoji || '👤'}</div>
                                            )}
                                        </div>
                                        <h3 className="text-sm font-bold" style={{ color: s.text }}>{item.title}</h3>
                                    </div>
                                    {/* Hover reveal */}
                                    <div className="absolute inset-0 flex flex-col items-center justify-center p-6 rounded-xl opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300"
                                        style={{ backgroundColor: accent }}>
                                        <h3 className="text-sm font-bold mb-2" style={{ color: dark ? '#fff' : '#1f2937' }}>{item.title}</h3>
                                        {item.description && <p className="text-xs line-clamp-3 mb-3" style={{ color: dark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.6)' }}>{item.description}</p>}
                                        <span className="text-xs font-semibold underline" style={{ color: dark ? '#fff' : '#1f2937' }}>
                                            {item.link_text || 'Conocer mas'} →
                                        </span>
                                    </div>
                                </a>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Cards variant (default) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s}/>
                <div className={`grid grid-cols-1 ${colClass} gap-6`}>
                    {items.map((item, i) => {
                        const accent = item.color || s.accent;
                        const img = resolveImage(item, 'image', mediaFolders);
                        return (
                            <a key={i} {...resolveLinkProps(item.link || '#')}
                                className={`group ${card} overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-lg ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms', borderTop: `3px solid ${accent}` }}>
                                <div className="p-6">
                                    <div className="flex items-center gap-4 mb-4">
                                        <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110" style={{ backgroundColor: hexToRgba(accent, 0.1) }}>
                                            {img ? (
                                                <img src={img} alt={item.title || ''} className="w-8 h-8 object-contain" loading="lazy"/>
                                            ) : item.icon ? (
                                                <div style={{ color: accent }}><SectionIcon name={item.icon} className="w-7 h-7"/></div>
                                            ) : (
                                                <div className="text-xl">{item.emoji || '👤'}</div>
                                            )}
                                        </div>
                                        <h3 className="text-base font-bold group-hover:underline" style={{ color: s.text }}>{item.title}</h3>
                                    </div>
                                    {item.description && <p className="text-sm line-clamp-3 mb-4" style={{ color: s.textSub }}>{item.description}</p>}
                                    <span className="inline-flex items-center text-sm font-semibold group-hover:gap-1.5 transition-all" style={{ color: accent }}>
                                        {item.link_text || 'Conocer mas'} <span className="group-hover:translate-x-1 transition-transform">→</span>
                                    </span>
                                </div>
                            </a>
                        );
                    })}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
        </section>
    );
}
